import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../context/PortalAuthContext';
import { ApiError } from '../utils/api';

const signInSchema = z.object({
  email: z.string().trim().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type SignInFormValues = z.infer<typeof signInSchema>;

function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = usePortalAuth();
  const [error, setError] = useState<string | null>(null);
  const infoMessage = (location.state as { message?: string } | null)?.message;
  const redirectPath = (
    location.state as { from?: { pathname?: string } } | null
  )?.from?.pathname;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values: SignInFormValues) => {
    setError(null);

    try {
      const user = await signIn(values.email, values.password);
      const defaultRoute = user.role === 'student'
        ? '/student-portal'
        : user.role === 'admin'
          ? '/admin-portal'
          : '/educator-portal';
      navigate(redirectPath || defaultRoute, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Unable to sign in');
      }
    }
  };

  return (
    <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
      <Card sx={{ width: '100%', maxWidth: 500 }}>
        <CardContent>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Sign In
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Sign in to continue.
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              {infoMessage && <Alert severity="info">{infoMessage}</Alert>}
              <TextField
                label="Email"
                type="email"
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                {...register('email')}
                required
                fullWidth
              />
              <TextField
                label="Password"
                type="password"
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
                {...register('password')}
                required
                fullWidth
              />
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </Button>
              <Button component={RouterLink} to="/register" variant="text">
                Need an account? Register
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default SignInPage;
